import { useEffect, useLayoutEffect, useRef, RefObject } from "react";

const MIN_SCALE = 0.85;
const LABEL_SLIDE_DISTANCE = 80;
const LABEL_VISIBILITY_POWER = 1.8;
const LABEL_FADE_DISTANCE = 0.55;
const SCROLL_SETTLE_DELAY_MS = 100;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false);

interface CachedChild {
  el: HTMLElement;
  width: number;
  centreX: number;
  label: HTMLElement | null;
}

interface ScrollState {
  debounceTimer: ReturnType<typeof setTimeout> | null;
  animationFrameId: number | null;
}

const measureChildren = (container: HTMLElement): CachedChild[] =>
  (Array.from(container.children) as HTMLElement[]).map((el) => {
    const width = el.offsetWidth;
    return {
      el,
      width,
      centreX: el.offsetLeft + width / 2,
      label: el.querySelector(
        '[data-testid="lookLabel"]'
      ) as HTMLElement | null,
    };
  });

const getClosestIndexFromCache = (
  scrollLeft: number,
  clientWidth: number,
  cache: CachedChild[]
): number => {
  const containerCentreX = scrollLeft + clientWidth / 2;
  let closestIndex = 0;
  let closestDistance = Infinity;
  for (let i = 0; i < cache.length; i++) {
    const distance = Math.abs(containerCentreX - cache[i].centreX);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = i;
    }
  }
  return closestIndex;
};

const applyAnimation = (
  scrollLeft: number,
  clientWidth: number,
  cache: CachedChild[],
  disableTransition: boolean = true
) => {
  const containerCentreX = scrollLeft + clientWidth / 2;

  cache.forEach(({ el, width, centreX, label }) => {
    const distanceFraction = width
      ? Math.min(Math.abs(containerCentreX - centreX) / width, 1)
      : 0;
    const scale = 1 - (1 - MIN_SCALE) * distanceFraction;
    el.style.transform = `scale(${scale})`;

    if (label) {
      const labelVisibility = Math.max(
        0,
        1 - distanceFraction / LABEL_FADE_DISTANCE
      );
      const easedVisibility = labelVisibility ** LABEL_VISIBILITY_POWER;

      if (disableTransition) {
        label.style.transition = "none";
      } else {
        label.style.transition = "";
      }
      label.style.opacity = String(easedVisibility);
      label.style.visibility = easedVisibility === 0 ? "hidden" : "visible";
      label.style.transform = `translateY(${
        LABEL_SLIDE_DISTANCE * (1 - easedVisibility)
      }px)`;
    }
  });
};

const REDUCED_MOTION_GAP = "16px";

const applyReducedMotionStyles = (
  container: HTMLElement,
  cache: CachedChild[],
  closestIndex: number
) => {
  container.style.columnGap = REDUCED_MOTION_GAP;
  cache.forEach(({ el, label }, i) => {
    el.style.transition = "none";
    el.style.transform = "scale(1)";

    if (label) {
      const isCentred = i === closestIndex;
      label.style.transition = "none";
      label.style.opacity = isCentred ? "1" : "0";
      label.style.visibility = isCentred ? "visible" : "hidden";
      label.style.transform = "translateY(0)";
    }
  });
};

const animateCarouselItems = (container: HTMLElement) => {
  const cache = measureChildren(container);

  if (prefersReducedMotion()) {
    const closestIndex = getClosestIndexFromCache(
      container.scrollLeft,
      container.clientWidth,
      cache
    );
    applyReducedMotionStyles(container, cache, closestIndex);
    return;
  }

  container.style.columnGap = "";
  applyAnimation(container.scrollLeft, container.clientWidth, cache);
};

export default function useCarouselAnimationScroll(
  selectedLookIndex: number,
  isOpen: boolean,
  onIndexChange: (index: number) => void,
  contentLoaded: boolean = true
): { containerRef: RefObject<HTMLElement> } {
  const containerRef = useRef<HTMLElement>(null);
  const scrollState = useRef<ScrollState>({
    debounceTimer: null,
    animationFrameId: null,
  });
  const isProgrammaticScroll = useRef(false);
  const skipNextProgrammaticScroll = useRef(false);
  const lastReportedIndex = useRef(selectedLookIndex);
  const hasInitialized = useRef(false);
  const layoutCache = useRef<CachedChild[] | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const container = containerRef.current;
    if (!container) return;
    const state = scrollState.current;

    const ensureCache = (): CachedChild[] => {
      if (!layoutCache.current) {
        layoutCache.current = measureChildren(container);
      }
      return layoutCache.current;
    };

    const handleScrollSettle = () => {
      const cache = ensureCache();
      const closestIndex = getClosestIndexFromCache(
        container.scrollLeft,
        container.clientWidth,
        cache
      );

      if (prefersReducedMotion()) {
        applyReducedMotionStyles(container, cache, closestIndex);
      } else {
        container.style.columnGap = "";
        // Restore CSS transitions after scroll settles
        applyAnimation(
          container.scrollLeft,
          container.clientWidth,
          cache,
          false
        );
      }

      layoutCache.current = null;
      if (closestIndex !== lastReportedIndex.current) {
        skipNextProgrammaticScroll.current = true;
        lastReportedIndex.current = closestIndex;
        onIndexChange(closestIndex);
      }
    };

    const handleScroll = () => {
      if (isProgrammaticScroll.current) return;

      if (state.animationFrameId === null) {
        state.animationFrameId = requestAnimationFrame(() => {
          const cache = ensureCache();
          if (prefersReducedMotion()) {
            const closestIndex = getClosestIndexFromCache(
              container.scrollLeft,
              container.clientWidth,
              cache
            );
            applyReducedMotionStyles(container, cache, closestIndex);
          } else {
            container.style.columnGap = "";
            applyAnimation(container.scrollLeft, container.clientWidth, cache);
          }
          state.animationFrameId = null;
        });
      }

      if (state.debounceTimer) clearTimeout(state.debounceTimer);
      state.debounceTimer = setTimeout(
        handleScrollSettle,
        SCROLL_SETTLE_DELAY_MS
      );
    };

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (state.debounceTimer) clearTimeout(state.debounceTimer);
      if (state.animationFrameId !== null) {
        cancelAnimationFrame(state.animationFrameId);
        state.animationFrameId = null;
      }
      layoutCache.current = null;
    };
  }, [isOpen, onIndexChange]);

  useEffect(() => {
    if (!isOpen) return;
    const container = containerRef.current;
    if (!container) return;

    const selectedElement = container.children[
      selectedLookIndex
    ] as HTMLElement;
    if (!selectedElement) return;

    if (skipNextProgrammaticScroll.current) {
      skipNextProgrammaticScroll.current = false;
      return;
    }

    isProgrammaticScroll.current = true;
    container.style.scrollBehavior = "auto";
    selectedElement.scrollIntoView({
      behavior: "instant",
      inline: "center",
      block: "nearest",
    });
    container.style.scrollBehavior = "";
    lastReportedIndex.current = selectedLookIndex;

    layoutCache.current = null;

    const frameId = requestAnimationFrame(() => {
      animateCarouselItems(container);
      isProgrammaticScroll.current = false;
    });

    return () => {
      cancelAnimationFrame(frameId);
      isProgrammaticScroll.current = false;
    };
  }, [selectedLookIndex, isOpen, contentLoaded]);

  useEffect(() => {
    if (isOpen) return;
    hasInitialized.current = false;
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || !contentLoaded || hasInitialized.current) return;
    const container = containerRef.current;
    if (!container) return;

    hasInitialized.current = true;
    animateCarouselItems(container);
  }, [isOpen, contentLoaded]);

  return { containerRef };
}
